import {
  Box,
  List,
  ListItem,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ButtonWithLoading from 'components/button_with_loading'
import { useDocumentTitle } from 'hooks'
import { useContext, useState } from 'react'
import { ExportToCsv } from 'export-to-csv-fix-source-map'
import {
  constructCdsSku,
  constructMarketplaceSku,
  constructRecordsSku,
  constructVintageBooksSku,
} from 'models'
import {
  get_and_increment_next_records_sku,
  get_and_increment_next_cds_sku,
  get_and_increment_next_marketplace_sku,
  get_and_increment_next_vintage_books_sku,
} from 'api/clothes_service'
import { UnauthorisedError } from 'errors'
import { UserContext } from 'base'
import { formatIsoDate } from 'utils'

enum ProductType {
  VINYL = 'Vinyl Records',
  CD = 'CDs',
  MARKETPLACE = 'Marketplace',
  VINTAGE_BOOKS = 'Vintage Books',
}

const SkuLabels = () => {
  useDocumentTitle('Download SKU Labels')
  const user = useContext(UserContext)
  const [qty, setQty] = useState('0')
  const [productType, setProductType] = useState(ProductType.VINYL)

  const [isGenerating, setIsGenerating] = useState<boolean>(false)

  const handleFormat = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setProductType(event.target.value as ProductType)
  }

  return (
    <>
      <Stack spacing={2}>
        <Stack>
          <Typography variant="h3">Download SKU Labels</Typography>
          <Typography variant="subtitle1">
            Download SKU labels in CSV format for printing:
            <List sx={{ listStyleType: 'disc', pl: 4, pt: 0 }} dense>
              <ListItem sx={{ display: 'list-item', pl: 0, pt: 0, pb: 0 }}>
                Online vinyl records (ON.REC.XXX)
              </ListItem>
              <ListItem sx={{ display: 'list-item', pl: 0, pt: 0, pb: 0 }}>
                CDs (ON.CD.XXX)
              </ListItem>
              <ListItem sx={{ display: 'list-item', pl: 0, pt: 0, pb: 0 }}>
                Marketplace (ON.MKT.XXX)
              </ListItem>
              <ListItem sx={{ display: 'list-item', pl: 0, pt: 0, pb: 0 }}>
                Vintage Books (ON.VB.XXX)
              </ListItem>
            </List>
          </Typography>
        </Stack>
        <Box
          component="form"
          onSubmit={async (event) => {
            setIsGenerating(true)
            event.preventDefault()

            const numQty = Number(qty)
            let nextSku
            let constructSku
            try {
              if (productType === ProductType.VINYL) {
                nextSku = await get_and_increment_next_records_sku(numQty)
                constructSku = constructRecordsSku
              } else if (productType === ProductType.CD) {
                nextSku = await get_and_increment_next_cds_sku(numQty)
                constructSku = constructCdsSku
              } else if (productType === ProductType.MARKETPLACE) {
                nextSku = await get_and_increment_next_marketplace_sku(numQty)
                constructSku = constructMarketplaceSku
              } else if (productType === ProductType.VINTAGE_BOOKS) {
                nextSku = await get_and_increment_next_vintage_books_sku(numQty)
                constructSku = constructVintageBooksSku
              } else {
                throw new Error(`Unexpected format: ${productType}`)
              }
            } catch (e: any) {
              if (e instanceof UnauthorisedError) {
                return user.setLoggedOut()
              }
              setIsGenerating(false)
              return
            }
            const data = []
            for (let i = 0; i < numQty; i++) {
              data.push({
                sku: constructSku(nextSku),
              })
              nextSku++
            }
            const isoDate = formatIsoDate(new Date())
            const productTypeUnderscore = productType.replaceAll(' ', '_')
            const csvExporter = new ExportToCsv({
              filename: `Thryft_SKU_Labels_${productTypeUnderscore}_${isoDate}`,
              useKeysAsHeaders: true,
            })
            csvExporter.generateCsv(data)
            setIsGenerating(false)
          }}
        >
          <Stack spacing={3} alignItems="flex-start">
            <TextField
              required
              select
              value={productType}
              label="Type"
              onChange={handleFormat}
            >
              {Object.values(ProductType).map((format) => (
                <MenuItem key={format} value={format}>
                  {format}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              type="number"
              inputProps={{ min: 0 }}
              label={`No. of SKU labels`}
              value={qty}
              onChange={(event) => setQty(event.target.value)}
              error={Number(qty) === 1}
              helperText={Number(qty) === 1 ? 'Must be at least 2' : null}
            />

            <ButtonWithLoading
              type="submit"
              variant="contained"
              disabled={
                qty === '' || !Number.isInteger(Number(qty)) || Number(qty) <= 1
              }
              isLoading={isGenerating}
            >
              Download Labels
            </ButtonWithLoading>
          </Stack>
        </Box>
      </Stack>
    </>
  )
}

export default SkuLabels
